/**
 * MonthlyLinearityComparisonCard.jsx
 *
 * Monthly version of the Linearity Comparison chart.
 * Shows month-over-month performance comparison with projections for current month.
 *
 * Data Source: Time Series projection of Closed Won (Monthly)
 * Redirect Dashboard: "Time Series projection of Closed Won (Monthly)"
 *
 * This is a thin wrapper component that:
 * 1. Uses the useMonthlyLinearityData hook for data fetching
 * 2. Renders using the shared D3Linearity chart component
 * 3. Wraps in RccCard with monthly-specific configuration
 *
 * Note: This card is only enabled for customers with monthly Time Series enabled (e.g., C0017).
 * The visibility is controlled via the INT_RCC_Cards_To_UI_Role_ID_Mapping database table.
 */

import React from "react";
import { useSelector } from "react-redux";
import D3Linearity from "./D3Linearity";
import RccCard from "../RccCard";
import { useMonthlyLinearityData } from "../hooks/useLinearityData";

/**
 * Monthly Linearity Comparison Card Component
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Card title (default: "Month Linearity Comparison")
 * @param {string} props.redirectToDashboard - Dashboard to redirect on zoom
 *   (default: "Time Series projection of Closed Won (Monthly)")
 * @returns {JSX.Element} The monthly linearity comparison card
 */
function MonthlyLinearityComparisonCard({ title, redirectToDashboard }) {
  const user = useSelector((state) => state.app?.user);

  // Use the monthly data hook - all fetching/transformation logic is encapsulated
  const { chartData, appliedFilters, isLoading, error } =
    useMonthlyLinearityData();

  const handleZoom = () => {
    console.log("Zooming Monthly Linearity Chart...");
  };

  return (
    <RccCard
      title={title || "Month Linearity Comparison"}
      isLoading={isLoading}
      onZoomClick={handleZoom}
      redirectToDashboard={
        redirectToDashboard || "Time Series projection of Closed Won (Monthly)"
      }
      appliedFilters={appliedFilters}
      focusVariant="current"
    >
      {/* Wrapper to ensure D3 Chart has relative positioning context */}
      <div
        style={{
          flex: 1,
          position: "relative",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {error ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              color: "#999",
            }}
          >
            {error}
          </div>
        ) : chartData.length > 0 ? (
          // D3Linearity works with both quarter and month data
          // The data structure uses "quarter" field for x-axis label regardless of period type
          <D3Linearity
            data={chartData}
            currencySymbol={user?.Currency || ""}
            periodType="month"
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              color: "#999",
            }}
          >
            No data available
          </div>
        )}
      </div>
    </RccCard>
  );
}

export default MonthlyLinearityComparisonCard;
